import { useEffect, useState } from 'react'
import { supabase } from '../supabase'
import { canUseOpportunityIntelligence } from '../permissions'
import { callClaude } from '../claude'

const SEARCH_STATE_KEY = 'cadence_oppintel_search_v1'

function loadSavedSearchState() {
  try {
    const raw = localStorage.getItem(SEARCH_STATE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

const NOTICE_TYPES = [
  { label: 'Open Tenders', value: 'notice' },
  { label: 'Contract Awards', value: 'award' },
]

const REGIONS = [
  '', 'North East', 'North West', 'Yorkshire and The Humber',
  'East Midlands', 'West Midlands', 'East of England', 'London',
  'South East', 'South West', 'Scotland', 'Wales', 'Northern Ireland'
]

const BUYER_TYPES = [
  { label: 'All Buyers', value: '' },
  { label: 'Central Government', value: 'CENTRAL_GOVERNMENT' },
  { label: 'Local Government', value: 'LOCAL_AUTHORITY' },
  { label: 'NHS & Health', value: 'NHS_AUTHORITY_TRUST' },
  { label: 'Education', value: 'EDUCATIONAL_AUTHORITY' },
  { label: 'Police & Emergency Services', value: 'POLICE_AUTHORITY' },
  { label: 'Housing', value: 'HOUSING_AUTHORITY' },
  { label: 'Utilities', value: 'UTILITIES' },
]

function OpportunityIntelligence({ session, profile, onNavigate }) {
  const savedState = loadSavedSearchState()
  const [keywords, setKeywords] = useState(savedState?.keywords || '')
  const [noticeType, setNoticeType] = useState(savedState?.noticeType || 'notice')
  const [dateRange, setDateRange] = useState(savedState?.dateRange || '30')
  const [region, setRegion] = useState(savedState?.region || '')
  const [buyerType, setBuyerType] = useState(savedState?.buyerType || '')
  const [minValue, setMinValue] = useState(savedState?.minValue || '')
  const [maxValue, setMaxValue] = useState(savedState?.maxValue || '')
  const [showAdvanced, setShowAdvanced] = useState(savedState?.showAdvanced || false)
  const [results, setResults] = useState(Array.isArray(savedState?.results) ? savedState.results : [])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [importing, setImporting] = useState(null)
  const [imported, setImported] = useState({})
  const [searched, setSearched] = useState(Boolean(savedState?.searched))
  const [totalHits, setTotalHits] = useState(Number.isFinite(savedState?.totalHits) ? savedState.totalHits : 0)
  const [qualifications, setQualifications] = useState({})
  const [qualifying, setQualifying] = useState({})
  const [qualifyingAll, setQualifyingAll] = useState(false)

  useEffect(() => {
    const nextState = {
      keywords,
      noticeType,
      dateRange,
      region,
      buyerType,
      minValue,
      maxValue,
      showAdvanced,
      results,
      searched,
      totalHits,
    }
    try {
      localStorage.setItem(SEARCH_STATE_KEY, JSON.stringify(nextState))
    } catch {
      // Ignore storage errors (quota/private mode) and keep UI responsive.
    }
  }, [keywords, noticeType, dateRange, region, buyerType, minValue, maxValue, showAdvanced, results, searched, totalHits])

  if (!canUseOpportunityIntelligence(profile)) return <div style={{ padding: '48px', textAlign: 'center', color: 'var(--slate)' }}>Opportunity Intelligence is available to Head of Bids and above.</div>

  const handleSearch = async () => {
    if (!keywords.trim()) return
    setLoading(true); setError(null); setResults([]); setSearched(true)
    setQualifications({}); setQualifying({})
    try {
      const fromDate = new Date()
      fromDate.setDate(fromDate.getDate() - parseInt(dateRange))

      const payload = {
        keywords: keywords.trim(),
        noticeType,
        publishedFrom: fromDate.toISOString().split('T')[0],
        size: 20
      }
      if (region) payload.region = region
      if (buyerType) payload.buyerType = buyerType
      if (minValue) payload.minValue = parseInt(minValue)
      if (maxValue) payload.maxValue = parseInt(maxValue)

      const response = await fetch('/api/fts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (!response.ok) throw new Error('API error: ' + response.status)
      const data = await response.json()
      setTotalHits(data.hitCount || 0)

      const mapped = (data.noticeList || []).map(n => {
        const item = n.item
        return {
          id: item.id,
          title: item.title,
          buyer: item.organisationName,
          description: item.description,
          value_low: item.valueLow || item.awardedValue || null,
          value_high: item.valueHigh || null,
          deadline: item.deadlineDate,
          published: item.publishedDate,
          status: item.noticeStatus,
          notice_type: item.noticeType,
          url: 'https://www.contractsfinder.service.gov.uk/Notice/' + item.id,
          winning_supplier: item.awardedSupplier || null,
          region: item.regionText || null,
          cpv: item.cpvDescription || null,
        }
      })
      setResults(mapped)
    } catch (err) {
      console.error('FTS error:', err)
      setError('Could not fetch results from Contracts Finder. Please try again.')
    }
    setLoading(false)
  }

  const handleQualify = async (notice) => {
    setQualifying(prev => ({ ...prev, [notice.id]: true }))
    try {
      const system = 'You are an expert bid qualification manager for a UK Government and Defence services company. Assess procurement notices and give a clear pursue/review/pass recommendation. Be concise and commercially focused. Respond in JSON only.'

      const prompt = 'Qualify this procurement opportunity for a Government and Defence services company.\n\nNotice: ' + notice.title + '\nBuyer: ' + notice.buyer + '\nValue: ' + (notice.value_low ? 'GBP ' + notice.value_low.toLocaleString() : 'Not specified') + (notice.value_high ? ' - ' + notice.value_high.toLocaleString() : '') + '\nScope: ' + (notice.description || 'Not provided').substring(0, 500) + '\nCPV Category: ' + (notice.cpv || 'Not specified') + '\nRegion: ' + (notice.region || 'Any') + '\n\nRespond with JSON:\n{\n  "recommendation": "Pursue" | "Review" | "Pass",\n  "rationale": "One sentence explanation",\n  "key_signals": ["signal 1", "signal 2"],\n  "risk_flags": ["flag 1"] or []\n}'

      const result = await callClaude({ system, prompt, max_tokens: 400 })
      const clean = result.replace(/```json/g, '').replace(/```/g, '').trim()
      const jsonMatch = clean.match(/\{[\s\S]*\}/)
      if (!jsonMatch) throw new Error('No JSON')
      const parsed = JSON.parse(jsonMatch[0])
      setQualifications(prev => ({ ...prev, [notice.id]: parsed }))
    } catch (err) {
      console.error('Qualify error:', err)
      setQualifications(prev => ({ ...prev, [notice.id]: { recommendation: 'Error', rationale: 'Could not generate assessment.' } }))
    }
    setQualifying(prev => ({ ...prev, [notice.id]: false }))
  }

  const handleQualifyAll = async () => {
    setQualifyingAll(true)
    const unqualified = results.filter(n => !qualifications[n.id])
    for (const notice of unqualified) {
      await handleQualify(notice)
    }
    setQualifyingAll(false)
  }

  const handleImport = async (notice) => {
    setImporting(notice.id)
    try {
      const { data, error } = await supabase.from('opportunities').insert([{
        title: notice.title,
        buyer: notice.buyer,
        description: notice.description ? notice.description.substring(0, 500) : null,
        contract_value_min: notice.value_low || null,
        contract_value_max: notice.value_high || notice.value_low || null,
        deadline: notice.deadline || null,
        stage: 'identified',
        organisation_id: profile.organisation_id,
        created_by: session.user.id,
        salesforce_url: notice.url || null,
      }]).select().single()
      if (!error) setImported(prev => ({ ...prev, [notice.id]: data.id }))
    } catch (err) { console.error('Import error:', err) }
    setImporting(null)
  }

  const fmtVal = (low, high) => {
    const val = high || low
    if (!val) return null
    if (val >= 1000000) return 'GBP ' + (val/1000000).toFixed(1) + 'M'
    if (val >= 1000) return 'GBP ' + Math.round(val/1000) + 'K'
    return 'GBP ' + val.toLocaleString()
  }

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null
  const daysUntil = d => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null

  const recColour = rec => {
    if (rec === 'Pursue') return 'var(--green)'
    if (rec === 'Review') return '#E67E22'
    if (rec === 'Pass') return 'var(--red)'
    return 'var(--slate)'
  }

  const aiEnabled = profile?.organisations?.ai_enabled

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px' }}>
      <div className="page-header">
        <p className="label">Intelligence</p>
        <h1>Opportunity Intelligence</h1>
        <p>Search Contracts Finder for live UK procurement notices and import directly into your pipeline.</p>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Keywords</label>
            <input type="text" value={keywords} onChange={e => setKeywords(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="e.g. managed services, cyber security, facilities management" autoFocus />
          </div>
          <button className="btn btn--primary" onClick={handleSearch} disabled={loading || !keywords.trim()} style={{ height: '42px', padding: '0 24px' }}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Notice Type</label>
            <select value={noticeType} onChange={e => setNoticeType(e.target.value)} style={{ width: '100%' }}>
              {NOTICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Published In Last</label>
            <select value={dateRange} onChange={e => setDateRange(e.target.value)} style={{ width: '100%' }}>
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="180">6 months</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: '12px' }}>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={{ background: 'none', border: 'none', color: 'var(--green)', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}
          >
            {showAdvanced ? '▲ Hide' : '▼ Advanced'} Filters
          </button>
        </div>

        {showAdvanced && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--green-light)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Region</label>
                <select value={region} onChange={e => setRegion(e.target.value)} style={{ width: '100%' }}>
                  {REGIONS.map(r => <option key={r} value={r}>{r || 'All Regions'}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Buyer Type</label>
                <select value={buyerType} onChange={e => setBuyerType(e.target.value)} style={{ width: '100%' }}>
                  {BUYER_TYPES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Min Value (GBP)</label>
                  <input type="number" value={minValue} onChange={e => setMinValue(e.target.value)} placeholder="e.g. 100000" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Max Value (GBP)</label>
                  <input type="number" value={maxValue} onChange={e => setMaxValue(e.target.value)} placeholder="e.g. 5000000" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && <div className="error-msg">{error}</div>}

      {loading && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--slate)' }}>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', color: 'var(--green-dark)', marginBottom: '8px' }}>Searching Contracts Finder...</div>
          <p style={{ fontSize: '0.875rem' }}>Fetching live procurement notices</p>
        </div>
      )}

      {!loading && searched && results.length === 0 && !error && (
        <div className="card">
          <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>No notices found. Try broader keywords or a longer date range.</p>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-dark)' }}>
              Showing {results.length} of {totalHits.toLocaleString()} notices
            </div>
            {aiEnabled && (
              <button
                onClick={handleQualifyAll}
                disabled={qualifyingAll}
                style={{ background: 'none', border: '1px solid var(--green-dark)', color: 'var(--green-dark)', padding: '6px 16px', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}
              >
                {qualifyingAll ? '⏳ Qualifying All...' : '✦ Qualify All'}
              </button>
            )}
          </div>

          {results.map(notice => {
            const days = daysUntil(notice.deadline)
            const val = fmtVal(notice.value_low, notice.value_high)
            const isImported = imported[notice.id]
            const isImporting = importing === notice.id
            const qual = qualifications[notice.id]
            const isQualifying = qualifying[notice.id]

            return (
              <div key={notice.id} style={{ background: 'var(--white)', border: '1px solid var(--green-light)', borderLeft: '4px solid ' + (qual ? recColour(qual.recommendation) : notice.status === 'Awarded' ? 'var(--slate)' : 'var(--green)'), marginBottom: '10px' }}>
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.125rem', fontWeight: 600, color: 'var(--green-dark)', lineHeight: 1.3 }}>{notice.title}</div>
                        <span style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 8px', background: notice.status === 'Awarded' ? 'var(--off-white)' : 'var(--green-pale)', color: notice.status === 'Awarded' ? 'var(--slate)' : 'var(--green)', border: '1px solid ' + (notice.status === 'Awarded' ? 'var(--silver)' : 'var(--green)'), letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0 }}>{notice.status}</span>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--slate)', marginBottom: '8px' }}>{notice.buyer}{notice.region && notice.region !== 'Any region' ? ' · ' + notice.region : ''}</div>
                      {notice.description && (
                        <div style={{ fontSize: '0.8125rem', color: 'var(--charcoal)', lineHeight: 1.5, marginBottom: '10px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {notice.description}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {val && <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--green-dark)', background: 'var(--green-pale)', padding: '2px 8px' }}>{val}</span>}
                        {notice.cpv && <span style={{ fontSize: '0.75rem', color: 'var(--slate)', fontStyle: 'italic' }}>{notice.cpv}</span>}
                        {notice.deadline && (
                          <span style={{ fontSize: '0.75rem', color: days !== null && days < 14 ? 'var(--red)' : days !== null && days < 30 ? '#E67E22' : 'var(--slate)', fontWeight: days !== null && days < 30 ? 700 : 400 }}>
                            {days !== null && days > 0 ? ('Deadline: ' + fmtDate(notice.deadline) + ' (' + days + ' days)') : days !== null && days <= 0 ? 'Deadline passed' : ('Deadline: ' + fmtDate(notice.deadline))}
                          </span>
                        )}
                        {notice.published && <span style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>Published: {fmtDate(notice.published)}</span>}
                        {notice.winning_supplier && <span style={{ fontSize: '0.75rem', color: 'var(--slate)', fontStyle: 'italic' }}>Awarded to: {notice.winning_supplier}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, alignItems: 'flex-end' }}>
                      {isImported ? (
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--green)', background: 'var(--green-pale)', padding: '6px 14px', border: '1px solid var(--green)' }}>✓ In Pipeline</span>
                      ) : (
                        <button onClick={() => handleImport(notice)} disabled={isImporting} className="btn btn--primary" style={{ fontSize: '0.8125rem', padding: '6px 16px' }}>
                          {isImporting ? 'Importing...' : '+ Add to Pipeline'}
                        </button>
                      )}
                      {aiEnabled && !qual && (
                        <button
                          onClick={() => handleQualify(notice)}
                          disabled={isQualifying}
                          style={{ background: 'none', border: '1px solid var(--green-dark)', color: 'var(--green-dark)', padding: '5px 14px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                        >
                          {isQualifying ? '⏳' : '✦ Qualify'}
                        </button>
                      )}
                      <a href={notice.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.75rem', color: 'var(--green)', textDecoration: 'underline' }}>View on CF →</a>
                    </div>
                  </div>
                </div>

                {qual && (
                  <div style={{ background: 'var(--green-dark)', padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <span style={{ fontSize: '0.625rem', fontWeight: 700, padding: '3px 10px', background: recColour(qual.recommendation), color: 'var(--white)', letterSpacing: '0.08em', textTransform: 'uppercase', flexShrink: 0, marginTop: '2px' }}>
                        {qual.recommendation}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.875rem', color: 'var(--white)', lineHeight: 1.5, marginBottom: qual.key_signals?.length ? '8px' : 0 }}>{qual.rationale}</div>
                        {qual.key_signals && qual.key_signals.length > 0 && (
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {qual.key_signals.map((s, i) => (
                              <span key={i} style={{ fontSize: '0.6875rem', color: 'var(--green-light)', background: 'rgba(255,255,255,0.1)', padding: '2px 8px' }}>{s}</span>
                            ))}
                            {qual.risk_flags && qual.risk_flags.map((f, i) => (
                              <span key={'r'+i} style={{ fontSize: '0.6875rem', color: '#E67E22', background: 'rgba(230,126,34,0.15)', padding: '2px 8px' }}>⚠ {f}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div className="actions" style={{ marginTop: '24px' }}>
        <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>← Dashboard</button>
      </div>
    </div>
  )
}

export default OpportunityIntelligence
